// Types mirroring the engine's API (see the engine's api/openapi.yaml).

export type Env = "sandbox" | "production";
export type Status = "active" | "suspended" | "closed";

export interface EnvCount {
  sandbox: number;
  production: number;
}

export interface CurrencyBrief {
  code: string;
  exponent: number;
  symbol: string | null;
}

export interface Currency extends CurrencyBrief {
  id: string;
  name: string;
  standard: boolean;
  status: string;
}

export interface Developer {
  id: string;
  name: string;
  contact_email: string;
  status: Status;
  /** Whether the developer can sign in with email and password. */
  has_password?: boolean;
  /** The developer confirmed their email address. */
  email_verified?: boolean;
  /** The engine requires verification and it hasn't happened yet: the API is limited until it does. */
  email_verification_pending?: boolean;
  /** The plan in force now. */
  plan?: PlanId;
  /** When the trial or paid period ends. */
  plan_expires_at?: string | null;
  /** On the free Pro trial. */
  plan_trial?: boolean;
  /** The free trial hasn't been used yet. */
  trial_available?: boolean;
  /** Paid features the plan unlocks. */
  features?: Feature[];
  /** The Stripe subscription, once the developer has paid. */
  billing?: BillingInfo | null;
  created_at: string;
  updated_at: string;
}

export interface BillingInfo {
  /** Stripe's status: active, trialing, past_due, canceled, unpaid… */
  status: string;
  /** When the paid period renews, or ends if cancel_at_period_end. */
  period_end: string | null;
  cancel_at_period_end: boolean;
}

export type PlanId = "free" | "pro";
export type Feature = "live_balances";

export interface Plan {
  id: PlanId;
  name: string;
  price_usd_monthly: number;
  features: Feature[];
}

export interface PlanCatalog {
  data: Plan[];
  features: { id: Feature; name: string; description: string }[];
  trial_days: number;
  /** Online payment (Stripe Checkout) is available. */
  billing_enabled?: boolean;
}

/** One account's balances, from the live balances snapshot or stream. */
export interface LiveBalance {
  account_id: string;
  account_number: string;
  account_name: string;
  account_type: string;
  owner_type: "player" | "system" | "merchant";
  system_role: string | null;
  status: string;
  bank_id: string;
  bank_name: string;
  player_id: string | null;
  external_player_id: string | null;
  player_name: string | null;
  currency: CurrencyBrief;
  posted_balance: number;
  available_balance: number;
  held_balance: number;
  /** Goes up with every change. */
  version: number;
  updated_at: string;
}

export interface LiveSnapshot {
  data: LiveBalance[];
  total: number;
  /** Stream changes from here. */
  cursor: string;
}

/** Returned by sign-up and sign-in: the developer and a session token. */
export interface SignInResult {
  developer: Developer;
  session: { id: string; token: string; expires_at: string };
  /** Sign-up only, when verification is required: whether the email went out. */
  verification_email_sent?: boolean;
}

/** A correct password while sign-in codes are on: the code is on its way. */
export interface LoginChallenge {
  id: string;
  /** The email address, partly hidden. */
  sent_to: string;
  expires_at: string;
  /** When another code can be requested. */
  resend_at: string;
}

/** POST /v1/auth/login: a session, or a code to enter first. */
export type LoginResult = (SignInResult & { login_code_required?: false }) | { login_code_required: true; challenge: LoginChallenge };

export interface AdminDeveloper extends Developer {
  games: number;
  active_credentials: number;
  active_api_keys: number;
  last_active_at: string | null;
}

export interface DevSession {
  id: string;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
  /** The session this browser is using. */
  current: boolean;
}

export interface Credential {
  id: string;
  label: string;
  prefix: string;
  secret?: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface Game {
  id: string;
  developer_id: string;
  name: string;
  slug: string;
  status: Status;
  created_at: string;
  updated_at: string;
}

export interface AdminGame extends Game {
  developer_name: string;
  banks: EnvCount;
  player_accounts: EnvCount;
  active_api_keys: EnvCount;
  transactions: EnvCount;
}

export interface ApiKey {
  id: string;
  game_id: string;
  environment: Env;
  label: string;
  prefix: string;
  secret?: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface AdminBank {
  id: string;
  environment: Env;
  name: string;
  bank_code: string;
  currency: string;
  exponent: number;
  symbol: string | null;
  status: string;
  branding: Record<string, unknown>;
  player_accounts: number;
  issued_minor: number;
  created_at: string;
}

export interface Clock {
  mode: "real_time" | "scaled" | "manual";
  speed: number;
  time_zone: string;
  game_time: string;
  game_day?: string;
  real_time?: string;
  environment?: Env;
}

export interface AdminGameDetail extends AdminGame {
  api_keys: ApiKey[];
  bank_list: AdminBank[];
  clocks: Clock[];
}

export interface Circulation {
  environment: Env;
  currency: string;
  exponent: number;
  symbol: string | null;
  custom: boolean;
  issued_minor: number;
  transfer_volume_24h: number;
  banks: number;
}

export interface Overview {
  developers: { total: number; active: number; suspended: number };
  games: { total: number; active: number; suspended: number };
  active_api_keys: EnvCount;
  banks: EnvCount;
  players: EnvCount;
  player_accounts: EnvCount;
  transactions: EnvCount;
  transactions_24h: EnvCount;
  active_holds: EnvCount;
  circulation: Circulation[];
  activity_14d: { day: string; sandbox: number; production: number }[];
  generated_at: string;
}

export interface AuditLog {
  id: string;
  developer_id: string | null;
  game_id: string | null;
  environment: Env | null;
  actor_type: string;
  actor_id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  correlation_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  game_name?: string | null;
  developer_name?: string | null;
}

export interface ReconDifference {
  kind: string;
  game_id: string;
  environment: string;
  bank_id?: string;
  account_id?: string;
  account_number?: string;
  transaction_id?: string;
  stored: number;
  expected: number;
}

export interface ReconReport {
  ok: boolean;
  accounts_checked: number;
  transactions_checked: number;
  differences: ReconDifference[];
  started_at: string;
  finished_at: string;
}

export interface JobRun {
  id: string;
  game_id: string;
  game_name: string;
  environment: Env;
  job: string;
  game_day: string;
  state: "running" | "completed" | "failed";
  result: Record<string, unknown>;
  started_at: string;
  finished_at: string | null;
}

export interface Account {
  id: string;
  bank_id: string;
  bank_code: string;
  owner_type: "player" | "system" | "merchant";
  player_id: string | null;
  merchant_id?: string | null;
  product_id?: string | null;
  interest_account_id?: string | null;
  system_role: string | null;
  account_type: string;
  account_number: string;
  account_name: string;
  currency: CurrencyBrief;
  status: "active" | "frozen" | "closed";
  posted_balance: number;
  available_balance: number;
  held_balance: number;
  created_at: string;
}

export interface Bank {
  id: string;
  environment: Env;
  name: string;
  slug: string;
  bank_code: string;
  base_currency: Currency;
  branding: Record<string, unknown>;
  feature_flags: Record<string, boolean>;
  config_version: number;
  status: string;
  created_at: string;
  system_accounts?: Account[];
}

export type FeeEvent = "transfer" | "hold_capture" | "account_maintenance" | "card_issuance" | "card_payment" | "savings_excess_withdrawal";
export type FeeKind = "fee" | "levy";
export type FeeModel = "fixed" | "percentage" | "combined";

/** A fee or levy a bank charges. Amounts are minor units; times are game time. */
export interface FeeRule {
  id: string;
  bank_id: string;
  code: string;
  name: string;
  event: FeeEvent;
  kind: FeeKind;
  model: FeeModel;
  fixed_minor: number;
  /** Basis points: 100 = 1%. */
  percentage_bps: number;
  min_fee_minor: number | null;
  max_fee_minor: number | null;
  min_amount_minor: number | null;
  max_amount_minor: number | null;
  payer: "sender";
  credit_account: "fee_income" | "levies_payable";
  status: "active" | "inactive";
  effective_from: string;
  effective_to: string | null;
  version: number;
  /** Where the rule stands at the current game time; only in_effect rules charge. */
  state: "in_effect" | "scheduled" | "ended" | "inactive";
  warnings: string[];
  created_at: string;
  updated_at: string;
}

export interface FeeRuleInput {
  code?: string;
  name?: string;
  event?: FeeEvent;
  kind?: FeeKind;
  model?: FeeModel;
  fixed_minor?: number;
  percentage_bps?: number;
  min_fee_minor?: number;
  max_fee_minor?: number;
  min_amount_minor?: number;
  max_amount_minor?: number;
  status?: "active" | "inactive";
  effective_from?: string;
  effective_to?: string;
  /** Start at the current game time. */
  start_now?: boolean;
  clear?: string[];
}

export interface FeeCharge {
  fee_rule_id: string;
  code: string;
  name: string;
  kind: FeeKind;
  amount_minor: number;
  credit_account: string;
}

export interface FeeQuote {
  bank_id: string;
  event: FeeEvent;
  amount_minor: number;
  fees: FeeCharge[];
  fees_total_minor: number;
  total_debit_minor: number;
  game_time: string;
}

export interface BankBooks {
  bank_id: string;
  currency: string;
  fee_income_minor: number;
  levies_payable_minor: number;
  loans_outstanding_minor?: number;
  interest_income_minor?: number;
  loan_losses_minor?: number;
  accounts: { system_role: string; account_name: string; account_number: string; balance_minor: number; balance_display: string }[];
}

export interface LimitPolicy {
  per_transaction_max: number | null;
  daily_max: number | null;
}

export interface Limits {
  bank_id: string;
  currency: string;
  game_day: string;
  game_funding_used_today: number;
  limits: Record<"player_funding" | "game_funding" | "account_transfer", LimitPolicy | null>;
}

export interface Player {
  id: string;
  external_player_id: string;
  display_name: string | null;
  status: string;
}

export interface Balance {
  account_id: string;
  posted_balance: number;
  available_balance: number;
  held_balance: number;
  posted_display: string;
  available_display: string;
  currency: CurrencyBrief;
}

export interface Entry {
  id: string;
  account_id: string;
  account_number: string;
  account_name: string;
  side: "debit" | "credit";
  amount_minor: number;
  balance_after: number;
  /** principal, fee or levy; fee and levy lines carry the fee's name. */
  kind?: "principal" | "fee" | "levy";
  description?: string;
}

export interface Transaction {
  id: string;
  session_id: string;
  type: string;
  status: string;
  amount_minor: number;
  amount_display: string;
  currency: CurrencyBrief;
  reference: string | null;
  narration: string | null;
  game_time: string;
  posted_at: string | null;
  entries: Entry[];
}

export interface HistoryItem {
  entry_id: string;
  transaction_id: string;
  session_id: string;
  type: string;
  status: string;
  direction: "debit" | "credit";
  amount_minor: number;
  amount_display: string;
  balance_after: number;
  /** principal, fee or levy. A reversal always undoes the whole transaction, never one line. */
  kind?: "principal" | "fee" | "levy";
  narration: string | null;
  reference: string | null;
  counterparty: { account_number: string; account_name: string; bank_code: string } | null;
  real_time: string;
  game_time: string;
}

export interface Hold {
  id: string;
  account_id: string;
  destination_account_id: string | null;
  amount_minor: number;
  captured_amount_minor: number | null;
  status: "active" | "captured" | "released" | "expired";
  reason: string;
  expires_at_game: string;
  created_game_time: string;
}

export interface NameEnquiry {
  bank_code: string;
  bank_name: string;
  account_number: string;
  account_name: string;
  status: string;
}

export interface Page<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export const ALL_SCOPES = [
  "bank:read",
  "bank:write",
  "account:read",
  "account:write",
  "transfer:create",
  "funding:create",
  "transaction:reverse",
  "limits:write",
  "webhooks:read",
  "webhooks:write",
  "merchants:write",
  "cards:read",
  "cards:write",
  "cards:charge",
  "deposits:write",
  "loans:write",
] as const;

export const DEFAULT_SCOPES = ["bank:read", "account:read", "account:write", "transfer:create"];
export const POWER_SCOPES = new Set(["funding:create", "transaction:reverse", "cards:charge"]);

// ---- Webhooks -------------------------------------------------------------------

export type WebhookEventType = "account.debited" | "account.credited";

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string | null;
  event_types: (WebhookEventType | "*")[];
  status: "active" | "disabled";
  disabled_reason: string | null;
  secret_prefix: string;
  /** Only when the endpoint is created or its secret rotated. */
  secret?: string;
  previous_secret_expires_at: string | null;
  failing_since: string | null;
  last_success_at: string | null;
  last_failure_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountAlert {
  account_id: string;
  account_number: string;
  account_name: string;
  transaction_id: string;
  transaction_type: string;
  amount_minor: number;
  charges: { kind: "fee" | "levy"; name: string; fee_rule_id: string | null; amount_minor: number }[];
  total_minor: number;
  total_display: string;
  currency: { code: string; exponent: number; symbol: string | null };
  balance_after: { posted_minor: number; available_minor: number };
  narration: string | null;
  counterparty: { type: "customer" | "bank"; account_name: string; account_number: string; bank_code: string } | null;
  sequence: number;
}

export interface WebhookEvent {
  id: string;
  type: WebhookEventType | "webhook.test";
  created_at: string;
  game_time: string | null;
  game_id: string;
  environment: Env;
  data: AccountAlert | Record<string, unknown>;
}

export interface WebhookDelivery {
  id: string;
  event_id: string;
  event_type: string;
  endpoint_id: string;
  endpoint_url: string;
  state: "pending" | "delivering" | "delivered" | "failed";
  attempts: number;
  next_attempt_at: string | null;
  last_attempt_at: string | null;
  last_status: number | null;
  last_error: string | null;
  last_response_ms: number | null;
  delivered_at: string | null;
  created_at: string;
}

// ---- Merchants and cards ---------------------------------------------------------

export interface Merchant {
  id: string;
  external_merchant_id: string;
  name: string;
  category: string;
  owner_player_id: string | null;
  status: "active" | "suspended";
  created_at: string;
}

export interface CardProgram {
  id: string;
  bank_id: string;
  name: string;
  bin: string;
  validity_months: number;
  auth_expiry_seconds: number;
  per_txn_limit_minor: number | null;
  daily_limit_minor: number | null;
  status: "active" | "inactive";
  created_at: string;
}

export interface CardProgramInput {
  name?: string;
  bin?: string;
  validity_months?: number;
  auth_expiry_seconds?: number;
  per_txn_limit_minor?: number;
  daily_limit_minor?: number;
  status?: "active" | "inactive";
  clear?: ("per_txn_limit_minor" | "daily_limit_minor")[];
}

export type CardStatus = "active" | "blocked" | "lost" | "stolen" | "expired" | "closed";

export interface Card {
  id: string;
  bank_id: string;
  account_id: string;
  program_id: string;
  masked_number: string;
  last4: string;
  expiry_month: number;
  expiry_year: number;
  name_on_card: string;
  status: CardStatus;
  status_reason: string | null;
  effective_per_txn_limit_minor: number | null;
  effective_daily_limit_minor: number | null;
  created_at: string;
}

export interface IssueCardResult {
  card: Card;
  card_number: string;
  cvv: string;
  fees_total_minor: number;
}

export interface CardAuthorization {
  id: string;
  card_id: string;
  card_last4: string;
  account_id: string;
  merchant: { id: string; name: string; category: string };
  merchant_account_id: string;
  status: "approved" | "declined" | "captured" | "released" | "expired" | "refunded";
  decline_code: string | null;
  decline_message: string | null;
  amount_minor: number;
  captured_minor: number;
  refunded_minor: number;
  currency: CurrencyBrief;
  reserved_fee_minor: number;
  expires_at_game: string | null;
  description: string | null;
  created_at: string;
}

// ---- Savings and fixed deposits ---------------------------------------------------

export interface DepositTier { from_minor: number; rate_bps: number }
export interface DepositTerm { days: number; rate_bps: number }
export type MaturityInstruction = "payout" | "rollover_principal" | "rollover_all";

export interface DepositProduct {
  id: string;
  bank_id: string;
  code: string;
  name: string;
  kind: "savings" | "fixed_deposit";
  rate_tiers: DepositTier[];
  tier_method: "whole_balance" | "banded";
  terms: DepositTerm[];
  day_count: "act_365" | "act_360";
  accrual_basis: "end_of_day" | "minimum_daily";
  payout_frequency: "monthly" | "quarterly" | "at_maturity";
  withholding_tax_bps: number;
  min_opening_minor: number;
  min_balance_minor: number;
  max_balance_minor: number | null;
  withdrawals_per_month: number | null;
  excess_withdrawal_action: "block" | "forfeit_interest" | "fee";
  early_forfeit_bps: number;
  early_penalty_fixed_minor: number;
  early_penalty_bps: number;
  default_maturity_instruction: MaturityInstruction;
  status: "active" | "inactive";
  version: number;
}

export type DepositProductInput = Partial<Omit<DepositProduct, "id" | "bank_id" | "version" | "max_balance_minor" | "withdrawals_per_month">> & {
  max_balance_minor?: number;
  withdrawals_per_month?: number;
  clear?: ("max_balance_minor" | "withdrawals_per_month")[];
};

export interface AccountInterest {
  account_id: string;
  product_name: string;
  payout_frequency: string;
  rate_bps: number;
  accrued_interest_minor: number;
  accrued_through: string;
  next_payout_day: string;
  forfeit_month: string | null;
  withdrawals_this_month: number;
  withdrawals_per_month: number | null;
  payouts: { transaction_id: string; gross_minor: number; narration: string; game_time: string }[];
}

export interface FixedDeposit {
  id: string;
  account_id: string;
  account_number: string;
  product_id: string;
  product_name: string;
  source_account_id: string;
  payout_account_id: string;
  principal_minor: number;
  rate_bps: number;
  term_days: number;
  start_day: string;
  maturity_day: string;
  maturity_instruction: MaturityInstruction;
  status: "active" | "matured" | "broken";
  terms_completed: number;
  interest_paid_minor: number;
  accrued_interest_minor: number;
  balance_minor: number;
}

export interface BreakQuote {
  principal_minor: number;
  accrued_interest_minor: number;
  forfeited_interest_minor: number;
  interest_paid_minor: number;
  tax_minor: number;
  penalty_minor: number;
  payout_minor: number;
}

// ---- developer dashboard (GET /v1/developer/overview) ----

export interface OverviewMoney {
  environment: Env;
  game_id: string;
  game_name: string;
  bank_id: string;
  bank_name: string;
  bank_code: string;
  currency: { code: string; exponent: number; symbol: string | null };
  issued_minor: number;
  moved_minor_7d: number;
  fee_income_minor: number;
  savings_minor: number;
  fixed_deposits_minor: number;
}

export interface OverviewEndpointIssue {
  id: string;
  url: string;
  environment: Env;
  game_id: string;
  game_name: string;
  status: "active" | "disabled";
  disabled_reason: string | null;
  failing_since: string | null;
}

export interface OverviewKeyIssue {
  id: string;
  label: string;
  prefix: string;
  environment: Env;
  game_id: string;
  game_name: string;
  issue: "never_used" | "idle" | "expiring";
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
}

export interface OverviewGame {
  id: string;
  name: string;
  status: string;
  banks: EnvCount;
  player_accounts: EnvCount;
  transactions_7d: EnvCount;
  last_transaction_at: string | null;
  live_keys: number;
}

export interface DeveloperOverview {
  game_id?: string;
  games: { total: number; active: number; suspended: number };
  players: EnvCount;
  player_accounts: EnvCount;
  new_accounts_7d: EnvCount;
  transactions_24h: EnvCount;
  transactions_7d: EnvCount;
  transactions_prev_7d: EnvCount;
  active_holds: EnvCount;
  active_cards: EnvCount;
  active_fixed_deposits: EnvCount;
  activity_14d: { day: string; sandbox: number; production: number }[];
  transaction_mix_7d: { type: string; sandbox: number; production: number }[];
  money: OverviewMoney[];
  webhooks: {
    endpoints: EnvCount;
    deliveries_24h: number;
    delivered_24h: number;
    failed_24h: number;
    pending: number;
    issues: OverviewEndpointIssue[];
  };
  cards: { authorizations_7d: number; declined_7d: number; decline_codes_7d: { code: string; count: number }[] };
  keys: { active: EnvCount; issues: OverviewKeyIssue[] };
  per_game: OverviewGame[];
  recent_activity: (Omit<AuditLog, "developer_id" | "correlation_id"> & { game_name: string | null })[];
  generated_at: string;
}

// ---- Loans ----------------------------------------------------------------------

export type RepaymentFrequency = "daily" | "weekly" | "monthly";
export type ScoreSource = "engine" | "game" | "blend";

export interface LoanScoreBand {
  min_score: number;
  /** 0 refuses this band. */
  max_principal_minor: number;
  rate_adjust_bps?: number;
}

export interface LoanProduct {
  id: string;
  bank_id: string;
  code: string;
  name: string;
  min_principal_minor: number;
  max_principal_minor: number;
  terms: { days: number; rate_bps: number }[];
  repayment_frequency: RepaymentFrequency;
  interest_method: "amortized" | "flat";
  origination_fee_bps: number;
  origination_fee_fixed_minor: number;
  late_fee_fixed_minor: number;
  late_fee_bps: number;
  grace_days: number;
  default_after_missed: number;
  partial_collection: boolean;
  score_source: ScoreSource;
  external_score_weight_bps: number;
  score_bands: LoanScoreBand[];
  max_installment_income_bps: number | null;
  first_loan_max_minor: number | null;
  max_active_loans: number;
  status: "active" | "inactive";
  version: number;
  created_at: string;
  updated_at: string;
}

export type LoanProductInput = Partial<Omit<LoanProduct, "id" | "bank_id" | "version" | "created_at" | "updated_at" | "max_installment_income_bps" | "first_loan_max_minor">> & {
  max_installment_income_bps?: number;
  first_loan_max_minor?: number;
  clear?: ("max_installment_income_bps" | "first_loan_max_minor")[];
};

export interface CreditScore {
  player_id: string;
  score: number;
  rating: "poor" | "fair" | "good" | "very_good" | "excellent";
  thin_file: boolean;
  factors: { factor: string; name: string; points: number; max: number; detail: string }[];
  tips: string[];
  facts: Record<string, number>;
  as_of_game_day: string;
}

export interface LoanInstallmentPlan {
  seq: number;
  due_day: string;
  principal_minor: number;
  interest_minor: number;
  total_minor: number;
}

export interface LoanQuote {
  eligible: boolean;
  reasons: { code: string; message: string }[];
  product_id: string;
  amount_minor: number;
  term_days: number;
  base_rate_bps: number;
  rate_bps: number;
  score: { score: number; rating: string; source: ScoreSource; engine_score: number | null; external_score: number | null };
  max_principal_minor: number;
  origination_fee_minor: number;
  net_disbursement_minor: number;
  repayment_frequency: RepaymentFrequency;
  installments_count: number;
  installment_minor: number;
  total_interest_minor: number;
  total_repayable_minor: number;
  schedule: LoanInstallmentPlan[];
}

export interface LoanInstallment {
  seq: number;
  due_day: string;
  principal_due_minor: number;
  interest_due_minor: number;
  late_fee_due_minor: number;
  paid_minor: number;
  owed_minor: number;
  status: "pending" | "partial" | "paid" | "missed" | "waived";
  missed: boolean;
  paid_day: string | null;
}

export interface Loan {
  id: string;
  bank_id: string;
  player_id: string;
  product_id: string;
  product_name: string;
  account_id: string;
  account_number: string;
  repayment_account_id: string;
  principal_minor: number;
  rate_bps: number;
  term_days: number;
  repayment_frequency: RepaymentFrequency;
  interest_method: string;
  installments_count: number;
  origination_fee_minor: number;
  score_used: number;
  score_source: ScoreSource;
  status: "active" | "paid_off" | "defaulted" | "written_off";
  missed_count: number;
  outstanding_principal_minor: number;
  arrears_minor: number;
  remaining_minor: number;
  next_due_day: string | null;
  next_due_minor: number;
  principal_paid_minor: number;
  interest_paid_minor: number;
  late_fees_paid_minor: number;
  written_off_minor: number;
  start_day: string;
  maturity_day: string;
  created_at: string;
  closed_at: string | null;
  installments?: LoanInstallment[];
}

export interface LoanPayoffQuote {
  loan_id: string;
  arrears_minor: number;
  principal_minor: number;
  interest_minor: number;
  late_fees_minor: number;
  interest_saved_minor: number;
  total_minor: number;
}
